import {
  addImagesToPayment,
  downloadPaymentsInvoice,
  getPaymentDetails,
  setAsPaid,
} from "core/Services/AccountantServices";
import { iPaymentDetails } from "core/kangarooTypes/iPaymentDetails";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Download,
  Package,
  Receipt,
  Store,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import Skeleton from "react-loading-skeleton";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "shared/Button";
import { formatDate } from "utils/DatesUtil";
import { getStatusColors } from "utils/statusColors";
import { USER_ROLE } from "utils/constant";
import { useTranslation } from "react-i18next";

const PaymentDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [payment, setPayment] = useState<iPaymentDetails | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [downloadingInvoice, setDownloadingInvoice] = useState<boolean>(false);
  const [openOrderIds, setOpenOrderIds] = useState<number[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isSettingPaid, setIsSettingPaid] = useState<boolean>(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string>("");

  useEffect(() => {
    const role = localStorage.getItem(USER_ROLE);
    if (role) {
      setUserRole(role);
    }
  }, []);

  const isReadOnly = userRole === "merchant";

  const onDrop = (acceptedFiles: File[]) => {
    setUploadedFiles((prev) => [...prev, ...acceptedFiles]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
      "application/pdf": [],
    },
    multiple: true,
  });

  const handleSetAsPaid = async () => {
    try {
      if (payment?.id) {
        setIsSettingPaid(true);
        await setAsPaid(payment.id);
        toast.success("Payment marked as paid.");
      }
    } catch (error) {
      toast.error("Failed to mark payment as paid.");
    } finally {
      setIsSettingPaid(false);
    }
  };
  const handleUploadFiles = async () => {
    try {
      if (payment?.id && uploadedFiles.length > 0) {
        setIsUploadingFiles(true);
        await addImagesToPayment(payment.id, uploadedFiles);
        setUploadedFiles([]);
        // Refetch payment details to show new files
        const response = await getPaymentDetails(payment.id.toString());
        setPayment(response.data);
        toast.success("Files uploaded successfully.");
      }
    } catch (error) {
      toast.error("Failed to upload files.");
    } finally {
      setIsUploadingFiles(false);
    }
  };

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        setIsLoading(true);
        if (id) {
          const response = await getPaymentDetails(id);
          setPayment(response.data);
        }
      } catch (error) {
        toast.error("Failed to load payment details.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchPaymentDetails();
  }, [id]);

  const handleDownloadInvoice = async () => {
    try {
      if (payment?.id) {
        setDownloadingInvoice(true);
        await downloadPaymentsInvoice(payment.id);
        toast.success("Invoice downloaded successfully.");
      }
    } catch (error) {
      toast.error("Failed to download invoice.");
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const toggleOrderAccordion = (orderId: number) => {
    setOpenOrderIds((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const isOrderOpen = (orderId: number) => {
    return openOrderIds.includes(orderId);
  };

  const PaymentDetailsSkeleton = () => (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex gap-2">
        <Skeleton width={100} height={40} />
        <Skeleton width={150} height={40} />
      </div>

      {/* Payment Details Skeleton */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <Skeleton width={200} height={24} className="mb-4" />
        <div className="space-y-3">
          <Skeleton width="60%" height={18} />
          <Skeleton width="40%" height={18} />
          <Skeleton width="50%" height={18} />
          <Skeleton width="45%" height={18} />
        </div>
      </div>

      {/* Merchant Information Skeleton */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <Skeleton width={220} height={24} className="mb-4" />
        <div className="space-y-3">
          <Skeleton width="50%" height={18} />
          <Skeleton width="70%" height={18} />
          <Skeleton width="40%" height={18} />
        </div>
      </div>

      {/* Orders Skeleton */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <Skeleton width={150} height={24} className="mb-4" />
        <div className="space-y-3 mb-4">
          <Skeleton width="40%" height={18} />
          <Skeleton width="30%" height={18} />
          <Skeleton width="50%" height={18} />
        </div>

        <Skeleton width={180} height={20} className="mb-3" />
        <div className="space-y-2 mb-4">
          <Skeleton width="60%" height={16} />
          <Skeleton width="50%" height={16} />
          <Skeleton width="70%" height={16} />
        </div>

        <Skeleton width={180} height={20} className="mb-3" />
        <div className="space-y-2 mb-4">
          <Skeleton width="40%" height={16} />
          <Skeleton width="45%" height={16} />
        </div>

        <Skeleton width={180} height={20} className="mb-3" />
        <div className="space-y-2">
          <Skeleton width="55%" height={16} />
          <Skeleton width="45%" height={16} />
          <Skeleton width="50%" height={16} />
          <Skeleton width="40%" height={16} />
          <Skeleton width="30%" height={16} />
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return <PaymentDetailsSkeleton />;
  }

  if (!payment) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center">
          <span className="font-medium">
            {t("paymentDetails.noPaymentDetails")}
          </span>
        </div>
        <Button
          onClick={() => window.history.back()}
          className="mt-4"
          variant="outlined"
          icon={<ArrowLeft size={16} />}
        >
          {t("paymentDetails.back")}
        </Button>
      </div>
    );
  }
  const getStatusColor = (status: string) => {
    const colors = getStatusColors(status);
    return `${colors.bg} ${colors.text}`;
  };

  const formatStatusText = (status: string) => {
    // Convert snake_case to Title Case
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="container mx-auto px-4 py-6 ">
      <div className="flex gap-3 mb-6">
        <Button
          onClick={() => window.history.back()}
          variant="outlined"
          icon={<ArrowLeft size={16} />}
          className="text-gray-700"
        >
          {t("paymentDetails.back")}
        </Button>
        {!isReadOnly && payment?.amount < 0 && (
          <Button
            onClick={handleDownloadInvoice}
            disabled={downloadingInvoice}
            icon={<Download size={16} />}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {downloadingInvoice
              ? t("paymentDetails.downloading")
              : t("paymentDetails.downloadInvoice")}
          </Button>
        )}
        {!isReadOnly && (
          <Button
            onClick={handleSetAsPaid}
            disabled={isSettingPaid}
            className="bg-green-600 hover:bg-green-700"
          >
            {isSettingPaid
              ? t("paymentDetails.settingAsPaid")
              : t("paymentDetails.setAsPaid")}
          </Button>
        )}
      </div>
      {/* Upload Invoice */}
      {!isReadOnly && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <h2 className="text-lg font-semibold">
              {t("paymentDetails.uploadInvoice")}
            </h2>
          </div>
          <div
            {...getRootProps()}
            className="border-dashed border-2 rounded-lg p-4 text-center border-gray-300 cursor-pointer"
          >
            <input {...getInputProps()} />
            <p className="text-sm text-gray-500">
              {t("paymentDetails.dragAndDropFiles")}
            </p>
            {uploadedFiles.length > 0 && (
              <ul className="mt-2 text-sm text-gray-700">
                {uploadedFiles.map((file, index) => (
                  <li key={index}>
                    {file.name} ({(file.size / 1024).toFixed(2)} KB)
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              onClick={handleUploadFiles}
              disabled={uploadedFiles.length === 0 || isUploadingFiles}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {isUploadingFiles
                ? t("paymentDetails.uploading")
                : t("paymentDetails.uploadFiles")}
            </Button>
          </div>
        </div>
      )}

      {/* Uploaded Documents Section */}
      {payment?.images && payment.images.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="mt-6 border-t pt-4">
            <h3 className="text-sm font-semibold mb-3">
              {t("paymentDetails.uploadedDocuments")}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {payment.images.map((imageUrl, index) => (
                <a
                  key={index}
                  href={imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                    {imageUrl.toLowerCase().endsWith(".pdf") ? (
                      <div className="h-24 bg-gray-100 flex items-center justify-center">
                        <Receipt size={32} className="text-gray-400" />
                      </div>
                    ) : (
                      <img
                        src={imageUrl}
                        alt={`${t("paymentDetails.document")} ${index + 1}`}
                        className="w-full h-24 object-cover"
                      />
                    )}
                    <div className="p-2 text-xs text-center text-gray-500 truncate">
                      {t("paymentDetails.document")} {index + 1}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Payment Summary Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center mb-4">
          <Receipt className="text-indigo-600 mr-2" size={20} />
          <h2 className="text-lg font-semibold">
            {t("paymentDetails.paymentSummary")}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">
              {t("paymentDetails.paymentId")}
            </p>
            <p className="font-medium">{payment.id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t("common.date")}</p>
            <p className="font-medium">{formatDate(payment.date)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t("common.amount")}</p>
            <p className="font-medium text-lg">JOD {payment.amount}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t("common.status")}</p>
            <span
              className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                payment.status
              )}`}
            >
              {formatStatusText(payment.status)}
            </span>
          </div>
        </div>
      </div>

      {/* Merchant Information */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center mb-4">
          <Store className="text-indigo-600 mr-2" size={20} />
          <h2 className="text-lg font-semibold">
            {t("paymentDetails.merchantInformation")}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">{t("paymentDetails.name")}</p>
            <p className="font-medium">
              {payment.merchant.first_name} {payment.merchant.last_name}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t("paymentDetails.phone")}</p>
            <p className="font-medium">{payment.merchant.phone_number}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm text-gray-500">
              {t("paymentDetails.businessName")}
            </p>
            <p className="font-medium">{payment.merchant.business_name}</p>
          </div>
        </div>
      </div>

      {/* Orders */}
      {payment.orders.map((order, index) => {
        const isOpen = isOrderOpen(order.id);
        return (
          <div key={index} className="bg-white rounded-lg shadow-md mb-6">
            <div
              className="flex items-center justify-between p-6 cursor-pointer"
              onClick={() => toggleOrderAccordion(order.id)}
            >
              <div className="flex items-center">
                <Package className="text-indigo-600 mr-2" size={20} />
                <h2 className="text-lg font-semibold">
                  {t("paymentDetails.order")} #{order.id}
                </h2>
              </div>
              <div className="flex items-center">
                <span
                  className={`inline-block px-2 py-1 rounded-full text-xs font-medium mr-3 ${getStatusColor(
                    order.status
                  )}`}
                >
                  {formatStatusText(order.status)}
                </span>
                {isOpen ? (
                  <ChevronUp className="text-gray-500" size={20} />
                ) : (
                  <ChevronDown className="text-gray-500" size={20} />
                )}
              </div>
            </div>
            {isOpen && (
              <div className="p-6 border-t border-gray-100">
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("paymentDetails.orderType")}
                    </p>
                    <p className="font-medium">{order.type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("paymentDetails.deliveryDate")}
                    </p>
                    <p className="font-medium">
                      {order.delivery_date
                        ? formatDate(order.delivery_date)
                        : "N/A"}
                    </p>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="border-t border-gray-100 pt-4 mb-6">
                  <div className="flex items-center mb-3">
                    <User className="text-indigo-600 mr-2" size={18} />
                    <h3 className="font-semibold">
                      {t("paymentDetails.customerInformation")}
                    </h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        {t("paymentDetails.name")}
                      </p>
                      <p className="font-medium">{order.customer.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {t("paymentDetails.phone")}
                      </p>
                      <p className="font-medium">{order.customer.phone}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">
                        {t("paymentDetails.address")}
                      </p>
                      <p className="font-medium">{order.customer.address}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {t("paymentDetails.city")}
                      </p>
                      <p className="font-medium">{order.customer.city}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {t("paymentDetails.area")}
                      </p>
                      <p className="font-medium">
                        {order.customer.area || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="border-t border-gray-100 pt-4 mb-6">
                  <h3 className="font-semibold mb-3">
                    {t("paymentDetails.items")}
                  </h3>
                  <div className="bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-12 px-4 py-2 text-sm font-medium text-gray-500">
                      <div className="col-span-5">
                        {t("paymentDetails.item")}
                      </div>
                      <div className="col-span-4">SKU</div>
                      <div className="col-span-3 text-right">
                        {t("paymentDetails.quantity")}
                      </div>
                    </div>
                    {order.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-12 px-4 py-3 border-t border-gray-100"
                      >
                        <div className="col-span-5 font-medium">
                          {item.name}
                        </div>
                        <div className="col-span-4 text-gray-600">
                          {item.sku || "N/A"}
                        </div>
                        <div className="col-span-3 text-right">
                          {item.quantity}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Information */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center mb-3">
                    <CreditCard className="text-indigo-600 mr-2" size={18} />
                    <h3 className="font-semibold">
                      {t("paymentDetails.paymentInformation")}
                    </h3>
                  </div>

                  <div className="flex justify-between mb-2">
                    <p className="text-gray-600">
                      {t("paymentDetails.paymentMethod")}
                    </p>
                    <p className="font-medium">{order.payment_method}</p>
                  </div>
                  <div className="flex justify-between mb-2">
                    <p className="text-gray-600">
                      {t("paymentDetails.orderAmount")}
                    </p>
                    <p className="font-medium">JOD {order.price}</p>
                  </div>
                  {order.pick_pack_fee > 0 && (
                    <div className="flex justify-between mb-2">
                      <p className="text-gray-600">Pick & Pack Fee</p>
                      <p className="font-medium text-blue-600">
                        JOD {order.pick_pack_fee.toFixed(3)}
                      </p>
                    </div>
                  )}
                  {order.services_fee > 0 && (
                    <div className="flex justify-between mb-2">
                      <p className="text-gray-600">Services Fee</p>
                      <p className="font-medium text-purple-600">
                        JOD {order.services_fee.toFixed(3)}
                      </p>
                    </div>
                  )}
                  <div className="flex justify-between mb-2">
                    <p className="text-gray-600">
                      {t("paymentDetails.deliveryFee")}
                    </p>
                    <p className="font-medium">JOD {order.delevery_fees}</p>
                  </div>
                  <div className="flex justify-between pt-2 border-t mt-2">
                    <p className="font-semibold text-gray-800">Grand Total</p>
                    <p className="font-bold text-green-700">
                      JOD{" "}
                      {(
                        order.grand_total || order.price + order.delevery_fees
                      ).toFixed(3)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PaymentDetails;
